import { useState, useEffect } from 'react';
import { getContentPerformanceAnalytics } from '../../services/contentPerformanceService';
import BarChart from '../charts/BarChart';

const ContentPerformanceTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [contentData, setContentData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getContentPerformanceAnalytics(30);
        setContentData(data);
      } catch (error) {
        console.error('Error loading content performance data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading content performance analytics...</p>
      </div>
    );
  }

  if (!contentData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No content performance data available</p>
      </div>
    );
  }

  // Content quality scores
  const qualityScoresData = {
    labels: contentData.contentQuality.slice(0, 15).map((c: any) => c.topicTitle.substring(0, 20)),
    datasets: [
      {
        label: 'Quality Score',
        data: contentData.contentQuality.slice(0, 15).map((c: any) => c.qualityScore),
        backgroundColor: '#10b981',
      },
    ],
  };

  // Completion rates
  const completionRatesData = {
    labels: contentData.contentQuality.slice(0, 15).map((c: any) => c.topicTitle.substring(0, 20)),
    datasets: [
      {
        label: 'Completion Rate (%)',
        data: contentData.contentQuality.slice(0, 15).map((c: any) => c.completionRate),
        backgroundColor: '#2563eb',
      },
    ],
  };

  // Content gaps
  const gapsData = {
    labels: contentData.contentGaps.slice(0, 10).map((g: any) => g.searchedTopic),
    datasets: [
      {
        label: 'Search Count',
        data: contentData.contentGaps.slice(0, 10).map((g: any) => g.searchCount),
        backgroundColor: '#f59e0b',
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Content Performance & Quality
      </h1>

      {/* AI Generation Metrics */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>AI Generation Success Rate</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {contentData.aiGenerationMetrics.successRate.toFixed(1)}%
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Generation Time</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {contentData.aiGenerationMetrics.avgGenerationTime.toFixed(1)}s
          </h2>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Module Generation Time</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            {contentData.aiGenerationMetrics.avgModuleGenerationTime > 0
              ? `${contentData.aiGenerationMetrics.avgModuleGenerationTime.toFixed(1)}s`
              : 'N/A'}
          </h2>
          <p style={{ margin: '0.5rem 0 0 0', color: '#999', fontSize: '0.75rem' }}>
            Per module average
          </p>
        </div>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.875rem' }}>Avg Cost per Generation</p>
          <h2 style={{ margin: '0.5rem 0', fontSize: '2rem', fontWeight: 'bold' }}>
            ${contentData.aiGenerationMetrics.avgCostPerGeneration.toFixed(4)}
          </h2>
        </div>
      </div>

      {/* Charts */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem',
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Content Quality Scores</h3>
          <BarChart data={qualityScoresData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Content Completion Rates</h3>
          <BarChart data={completionRatesData} height={300} />
        </div>

        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}>
          <h3 style={{ marginTop: 0 }}>Content Gaps (Searched but Not Found)</h3>
          <BarChart data={gapsData} height={300} />
        </div>
      </div>

      {/* Content Gaps Table */}
      {contentData.contentGaps.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
          marginBottom: '2rem',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Content Gaps - Opportunities
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Search Count</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {contentData.contentGaps.map((gap: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{gap.searchedTopic}</td>
                  <td style={{ padding: '1rem' }}>{gap.searchCount}</td>
                  <td style={{ padding: '1rem' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      backgroundColor: '#fef3c7',
                      color: '#92400e',
                      fontSize: '0.875rem',
                    }}>
                      Missing Content
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Low Performing Content */}
      {contentData.lowPerformingContent.length > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Low Performing Content (Completion Rate &lt; 30%)
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Topic</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Completion Rate</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Quality Score</th>
              </tr>
            </thead>
            <tbody>
              {contentData.lowPerformingContent.map((content: any, index: number) => (
                <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                  <td style={{ padding: '1rem' }}>{content.topicTitle}</td>
                  <td style={{ padding: '1rem', color: '#ef4444' }}>
                    {content.completionRate.toFixed(1)}%
                  </td>
                  <td style={{ padding: '1rem' }}>{content.qualityScore}/5</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ContentPerformanceTab;

